﻿import axios from 'axios'
import router from '@/router'

// 创建axios实例
const api = axios.create({
  baseURL: import.meta.env.VITE_API_BASE_URL || '',
  timeout: 60000,
  headers: {
    'Content-Type': 'application/json'
  }
})

// 请求拦截器 - 添加token
api.interceptors.request.use(
  (config) => {
    // 从localStorage获取token
    const token = localStorage.getItem('token')

    if (token) {
      config.headers.Authorization = `Bearer ${token}`
    } else {
      console.warn('[API] No token found in localStorage')
    }

    return config
  },
  (error) => {
    return Promise.reject(error)
  }
)

// 响应拦截器 - 错误处理
api.interceptors.response.use(
  (response) => {
    // 成功响应直接返回
    return response
  },
  (error) => {
    // 处理错误响应
    if (error.response) {
      const { status, data } = error.response

      switch (status) {
        case 401:
          // 未授权 - token过期或无效
          // 清除本地token
          localStorage.removeItem('token')

          // 重定向到登录页
          if (router.currentRoute.value.name !== 'Login') {
            router.push({
              name: 'Login',
              query: { redirect: router.currentRoute.value.fullPath }
            })
          }
          break

        case 403:
          // 禁止访问 - 权限不足
          showError('您没有权限执行此操作')
          break

        case 404:
          // 资源未找到
          showError('请求的资源不存在')
          break

        case 422:
          // 验证错误
          if (data.detail) {
            if (Array.isArray(data.detail)) {
              // FastAPI验证错误格式
              const errors = data.detail.map(err => err.msg).join(', ')
              showError(`数据验证失败: ${errors}`)
            } else {
              showError(data.detail)
            }
          }
          break

        case 429:
          // 请求过于频繁
          showError('请求过于频繁，请稍后再试')
          break

        case 500:
          // 服务器错误
          showError('服务器错误，请稍后再试')
          break

        case 503:
          // 服务不可用
          showError('服务暂时不可用，请稍后再试')
          break

        default:
          // 其他错误
          showError(data?.detail || '请求失败，请稍后再试')
      }
    } else if (error.request) {
      // 请求已发送但没有收到响应
      if (error.code === 'ECONNABORTED' || error.message?.includes('timeout')) {
        showError('请求超时，AI正在处理中，请稍后刷新重试')
      } else {
        showError('网络错误，请检查网络连接')
      }
    } else {
      // 请求配置出错
      showError('请求配置错误')
    }

    return Promise.reject(error)
  }
)

// 错误提示函数（简单实现，后续可以用UI库的toast组件替换）
function showError(message) {
  // 这里可以集成UI库的通知组件
  // 暂时使用console.error
  console.error(message)

  // 可以在这里添加全局错误提示组件的调用
  // 例如: useToast().error(message)
}

// Strategy Groups API
export const strategyGroupsApi = {
  // Get all strategy groups
  getGroups: () => api.get('/api/strategy-groups'),

  // Get strategy group by ID
  getGroup: (id) => api.get(`/api/strategy-groups/${id}`),

  // Create new strategy group
  createGroup: (data) => api.post('/api/strategy-groups', data),

  // Update strategy group
  updateGroup: (id, data) => api.put(`/api/strategy-groups/${id}`, data),

  // Delete strategy group
  deleteGroup: (id) => api.delete(`/api/strategy-groups/${id}`),

  // Move stock to another group
  moveStock: (stockCode, sourceGroupId, targetGroupId) => api.post('/api/strategy-groups/move-stock', {
    stock_code: stockCode,
    source_group_id: sourceGroupId,
    target_group_id: targetGroupId
  })
}

// Strategy Files API
export const strategyFilesApi = {
  // Get all strategy files
  getFiles: () => api.get('/api/strategy-files'),

  // Get strategy file by ID
  getFile: (id) => api.get(`/api/strategy-files/${id}`),

  // Upload new strategy file
  uploadFile: (file, displayName) => {
    const formData = new FormData()
    formData.append('file', file)
    formData.append('display_name', displayName)
    return api.post('/api/strategy-files/upload', formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    })
  },

  // Update strategy file content
  updateFile: (id, content, changeDescription) => api.put(`/api/strategy-files/${id}`, {
    content,
    change_description: changeDescription
  }),

  // Save as new strategy file
  saveAs: (id, newName, displayName) => api.post(`/api/strategy-files/${id}/save-as`, {
    new_name: newName,
    display_name: displayName
  }),

  // Delete strategy file
  deleteFile: (id) => api.delete(`/api/strategy-files/${id}`),

  // Get version history
  getVersions: (id) => api.get(`/api/strategy-files/${id}/versions`),

  // Rollback to specific version
  rollback: (id, version) => api.post(`/api/strategy-files/${id}/rollback`, { version }),

  // Get visualized strategy
  visualize: (id) => api.get(`/api/strategy-files/${id}/visualize`),

  // Validate YAML content
  validate: (content) => api.post('/api/strategy-files/validate', { content })
}

// Positions API (Enhanced)
export const positionsApi = {
  // Get all positions
  getPositions: () => api.get('/api/positions'),

  // Get positions grouped by strategy group
  getPositionsByGroup: () => api.get('/api/positions/by-group'),

  // 获取单个持仓卖点
  getSellPlan: (positionId) => api.get(`/api/positions/${positionId}/sell-plan`),

  // 更新单个持仓卖点
  updateSellPlan: (positionId, payload) => api.put(`/api/positions/${positionId}/sell-plan`, payload),

  // 删除单个持仓卖点
  deleteSellPlan: (positionId) => api.delete(`/api/positions/${positionId}/sell-plan`),

  // 获取单个持仓买点
  getBuyPlan: (positionId) => api.get(`/api/positions/${positionId}/buy-plan`),

  // 更新单个持仓买点
  updateBuyPlan: (positionId, payload) => api.put(`/api/positions/${positionId}/buy-plan`, payload),

  // 删除单个持仓买点
  deleteBuyPlan: (positionId) => api.delete(`/api/positions/${positionId}/buy-plan`),

  // Move position to another group
  movePosition: (positionId, targetGroupId) => api.post(`/api/positions/${positionId}/move`, {
    target_group_id: targetGroupId
  }),

  // Sync positions
  syncPositions: (data) => api.post('/api/positions/sync', data)
}

// Trade Reviews API
export const reviewsApi = {
  // Get pending trades for review
  getPendingTrades: (startDate, endDate) => api.get('/api/reviews/pending', {
    params: { start_date: startDate, end_date: endDate }
  }),

  // Mark a single trade
  markTrade: (tradeId, isCorrect, errorCategory, errorReason, detailedNotes) =>
    api.post('/api/reviews/mark', {
      trade_id: tradeId,
      is_correct: isCorrect,
      error_category: errorCategory,
      error_reason: errorReason,
      detailed_notes: detailedNotes
    }),

  // Batch mark trades
  batchMark: (reviews) => api.post('/api/reviews/batch-mark', { reviews }),

  // Get review statistics
  getStatistics: (startDate, endDate) => api.get('/api/reviews/statistics', {
    params: { start_date: startDate, end_date: endDate }
  }),

  // Request AI analysis
  requestAiAnalysis: (sessionDate, tradeIds) => api.post('/api/reviews/ai-analyze', {
    session_date: sessionDate,
    trade_ids: tradeIds
  }, { timeout: 300000 }),

  // Apply AI suggestion
  applySuggestion: (sessionId, strategyFileId, suggestionIndex, createBackup) =>
    api.post('/api/reviews/apply-suggestion', {
      session_id: sessionId,
      strategy_file_id: strategyFileId,
      suggestion_index: suggestionIndex,
      create_backup: createBackup
    }),

  // Get review sessions
  getSessions: (limit) => api.get('/api/reviews/sessions', {
    params: { limit }
  }),

  // Get session by ID
  getSession: (sessionId) => api.get(`/api/reviews/sessions/${sessionId}`)
}

// Trading Psychology Training API
export const trainingApi = {
  // 中文注释：训练目录固定放前端，后端接口只处理用户训练记录、统计和 AI 辅助结果。
  getOverview: () => api.get('/api/training/overview'),

  createSession: (data) => api.post('/api/training/sessions', data),

  updateSession: (id, data) => api.patch(`/api/training/sessions/${id}`, data),

  getSessions: (params = {}) => api.get('/api/training/sessions', { params }),

  getStatistics: (params = {}) => api.get('/api/training/statistics', { params }),

  getWeeklySummary: (weekStart) => api.get('/api/training/weekly-summaries', {
    params: weekStart ? { week_start: weekStart } : {}
  }),

  saveWeeklySummary: (data) => api.post('/api/training/weekly-summaries', data),

  getRecommendation: () => api.get('/api/training/recommendation'),

  getDisciplineAdvice: () => api.get('/api/training/discipline-advice')
}

// Sector Search API (P2 Enhancement)
export const sectorSearchApi = {
  // Search sectors with fuzzy matching
  searchSectors: (keyword, sectorType = null, limit = 20) => api.get('/api/sectors/leaders/sectors/search', {
    params: {
      q: keyword,
      sector_type: sectorType,
      limit: limit
    }
  }),

  // Get all sectors
  getAllSectors: () => api.get('/api/sectors/leaders/sectors'),

  // Search sector leaders
  searchLeaders: (sectorName, analysisDate = null, forceRefresh = false) =>
    api.post('/api/sectors/leaders/search', {
      sector_name: sectorName,
      analysis_date: analysisDate,
      force_refresh: forceRefresh
    }),

  // Get leader history
  getLeaderHistory: (sectorName = null, stockCode = null, startDate = null, endDate = null, limit = 100) =>
    api.get('/api/sectors/leaders/history', {
      params: {
        sector_name: sectorName,
        stock_code: stockCode,
        start_date: startDate,
        end_date: endDate,
        limit: limit
      }
    }),

  // Add leader to pool
  addToPool: (stockCode, candidateType = null, entryRules = null, entryRuleParams = null, notes = null) =>
    api.post(`/api/sectors/leaders/${stockCode}/add-to-pool`, {
      candidate_type: candidateType,
      entry_rules: entryRules,
      entry_rule_params: entryRuleParams,
      notes: notes
    }),

  // Add leader to watchlist
  addToWatchlist: (stockCode, strategyGroupId = null) =>
    api.post(`/api/sectors/leaders/${stockCode}/add-to-watchlist`, {
      strategy_group_id: strategyGroupId
    }),

  // Get comprehensive ranking (top and bottom sectors)
  getComprehensiveRanking: () => api.get('/api/sectors/leaders/sectors/ranking/comprehensive'),

  // Get five day strongest sectors (legacy)
  getFiveDayStrongestSectors: (limit = 5) => api.get(`/api/sectors/leaders/sectors/ranking/five-day-strongest?limit=${limit}`),

  // Get N day strongest sectors (3/5/10 days)
  getNDayStrongestSectors: (days = 5, limit = 5) => api.get('/api/sectors/leaders/sectors/ranking/n-day-strongest', {
    params: { days, limit }
  }),

  // Get global strongest sectors based on theme resonance
  getGlobalStrongestSectors: (limit = 5) => api.get('/api/sectors/leaders/sectors/ranking/global-strongest', {
    params: { limit }
  }),

  // Get global top leaders (TOP10 across all sectors + KCB TOP10)
  getGlobalTopLeaders: (analysisDate = null) => api.get('/api/sectors/leaders/global-top-leaders', {
    params: {
      analysis_date: analysisDate
    }
  }),

  // Get KCB all stocks 3-day top 10 (pure volume ranking)
  getKcbAll3dTop10: (analysisDate = null) => api.get('/api/sectors/leaders/global-top-leaders/kcb-all3d', {
    params: {
      analysis_date: analysisDate
    }
  })
}

// Strategy Matching API
export const strategyMatchingApi = {
  // Get strategies for a strategy group
  getGroupStrategies: (groupId) => api.get(`/api/strategy-groups/${groupId}/strategies`),

  // Update watchlist stock strategy
  updateWatchlistStrategy: (stockId, data) => api.put(`/api/stocks/watchlist/${stockId}/strategy`, data),

  // Update candidate stock strategy
  updateCandidateStrategy: (candidateId, data) => api.put(`/api/candidates/${candidateId}/strategy`, data),

  // Get stock signals
  getStockSignals: (stockCode) => api.get(`/api/signals/${stockCode}`),

  // Get signals for multiple stocks (batch)
  getSignalsBatch: (stockCodes) => api.get('/api/signals/batch', {
    params: { stock_codes: stockCodes }
  })
}

// Signals API
export const signalsApi = {
  // 获取当天短线拉升评分，用于首页在股票名称后显示最近一次评分
  getShortRiseScores: (limit = 50) => api.get('/api/signals/short-rise/scores', {
    params: { limit }
  }),

  // 按点击时刻实时重算单只股票的最近一次拉升/跳水评分
  getRealtimeScore: (stockCode) => api.get(`/api/signals/short-rise/realtime-score/${stockCode}`),

  // 获取当天触发过重点观察信号的自选股，用于首页“当日观察”开关过滤
  getTodayObservationStocks: () => api.get('/api/signals/today-observation/stocks')
}

// Stocks API
export const stocksApi = {
  // 删除自选股（推荐使用此方法）
  removeFromWatchlistWithBody: (stockCode, data) => api.post(`/api/stocks/watchlist/${stockCode}/remove`, data),

  // 删除自选股（Query参数版本）
  removeFromWatchlist: (stockCode, clearPosition = false, sellPrice = null) => {
    const params = { clear_position: clearPosition }
    if (sellPrice !== null) {
      params.sell_price = sellPrice
    }
    return api.delete(`/api/stocks/watchlist/${stockCode}`, { params })
  },

  // 批量删除自选股
  batchRemoveFromWatchlist: (data) => api.post('/api/stocks/watchlist/batch-remove', data),

  // 获取自选股状态列表
  getWatchlistStatus: () => api.get('/api/stocks/watchlist/status'),

  // 批量保存重点观察自选股
  updateKeyObservation: (items) => api.post('/api/stocks/watchlist/key-observation', { items }),

  // 识别重点观察截图，返回按图片顺序解析出的股票列表
  parseKeyObservationImage: (file) => {
    const formData = new FormData()
    formData.append('file', file)
    return api.post('/api/stocks/watchlist/key-observation/parse-image', formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    })
  },

  // 用用户确认后的识别结果整体替换重点观察股，后端会自动补齐不在自选股中的有效股票
  replaceKeyObservation: (stockCodes) => api.post('/api/stocks/watchlist/key-observation/replace', {
    stock_codes: stockCodes
  }),

  // 获取单只股票状态
  getStockStatus: (stockCode) => api.get(`/api/stocks/watchlist/${stockCode}/status`),

  // 获取股票所属板块及该板块的龙头股
  getSectorLeaders: (stockCode) => api.get(`/api/stocks/sector-leaders/${stockCode}`),

  // 查询股权登记日（今日若交易日 + 下一交易日）
  getEquityRecordRegistration: (stockCodes) =>
    api.get('/api/stocks/watchlist/equity-record-registration', {
      params: {
        codes: (stockCodes || []).join(','),
      },
    }),

  // 查询未来一周内较大比例解禁
  getShareUnlockAlert: (stockCodes, options = {}) =>
    api.get('/api/stocks/watchlist/share-unlock-alert', {
      params: {
        codes: (stockCodes || []).join(','),
        days_forward: options.daysForward ?? 7,
        min_ratio_percent: options.minRatioPercent ?? 5,
      },
    }),
}

// News API
export const newsApi = {
  // Get hot news list
  getHotNews: (params) => api.get('/api/news/hot', { params }),

  // 获取未读热点新闻数量（轻量级角标接口）
  getUnreadCount: (params) => api.get('/api/news/unread-count', { params }),

  // Manually refresh news
  refreshNews: (analyze = true) => api.post(`/api/news/refresh?analyze=${analyze}`),

  // Trigger pending analysis
  analyzePending: (limit = 20) => api.post(`/api/news/analyze/pending?limit=${limit}`),

  // Mark news as read
  markRead: (newsIds) => api.post('/api/news/read', { news_ids: newsIds }),

  // Mark currently visible hot news as read on the backend
  markVisibleRead: (data) => api.post('/api/news/read/visible', data),

  // 重点关注个股：与持仓股同等的新闻匹配/推送行为（持仓股除外）
  getFocusStocks: () => api.get('/api/news/focus-stocks'),
  addFocusStock: (stockCode, stockName) => api.post('/api/news/focus-stocks', {
    stock_code: stockCode,
    stock_name: stockName || ''
  }),
  removeFocusStock: (stockCode) => api.delete(`/api/news/focus-stocks/${stockCode}`)
}

// Fund API
export const fundApi = {
  // 搜索基金
  searchFunds: (keyword) => api.get('/api/fund/search', { params: { keyword } }),

  // 获取基金详情
  getFundInfo: (fundCode) => api.get(`/api/fund/${fundCode}`),

  // 获取净值历史。默认取约一年半的交易记录，再由图表按自然周期截取展示。
  getNavHistory: (fundCode, days = 370) => api.get(`/api/fund/nav-history/${fundCode}`, { params: { days } }),

  // 获取实时估值
  getValuation: (fundCode) => api.get(`/api/fund/${fundCode}/valuation`),

  // 持仓相关
  getPositions: (groupId = null) => api.get('/api/fund/positions/list', { params: { group_id: groupId } }),
  createPosition: (data) => api.post('/api/fund/positions', data),
  updatePosition: (positionId, data) => api.put(`/api/fund/positions/${positionId}`, data),
  deletePosition: (positionId) => api.delete(`/api/fund/positions/${positionId}`),

  // 交易记录
  addTransaction: (positionId, data) => api.post(`/api/fund/positions/${positionId}/transaction`, data),
  getTransactions: (fundCode = null, limit = 50, status = null) => api.get('/api/fund/transactions', { params: { fund_code: fundCode, limit, status } }),
  updateTransaction: (transactionId, data) => api.put(`/api/fund/transactions/${transactionId}`, data),
  deleteTransaction: (transactionId) => api.delete(`/api/fund/transactions/${transactionId}`),
  getRecurringInvestments: () => api.get('/api/fund/recurring-investments'),
  createRecurringInvestment: (positionId, data) => api.post(`/api/fund/positions/${positionId}/recurring-investments`, data),
  updateRecurringInvestment: (planId, data) => api.patch(`/api/fund/recurring-investments/${planId}`, data),
  updateRecurringInvestmentStatus: (planId, status) => api.patch(`/api/fund/recurring-investments/${planId}/status`, { status }),

  // 分组相关
  getGroups: () => api.get('/api/fund/groups/list'),
  createGroup: (data) => api.post('/api/fund/groups', data),
  updateGroup: (groupId, data) => api.put(`/api/fund/groups/${groupId}`, data),
  deleteGroup: (groupId) => api.delete(`/api/fund/groups/${groupId}`),

  // 搜索历史
  getSearchHistory: (limit = 20) => api.get('/api/fund/search-history', { params: { limit } }),
  saveSearchHistory: (data) => api.post('/api/fund/search-history', data),

  // 统计相关
  getProfitLossStatistics: (params) => api.get('/api/fund/statistics/profit-loss', { params }),

  // 交易结算执行
  executeTransactions: () => api.post('/api/fund/transactions/execute'),
  
  // 自选相关
  getWatchlist: () => api.get('/api/fund/watchlist'),
  addToWatchlist: (data) => api.post('/api/fund/watchlist', data),
  removeFromWatchlist: (fundCode) => api.delete(`/api/fund/watchlist/${fundCode}`)
}

// Intraday Selector API
export const intradaySelectorApi = {
  getResults: (session = null) => api.get('/api/intraday-selector/results', {
    params: session ? { session } : {}
  }),
  getSummary: () => api.get('/api/intraday-selector/results/summary')
}

// 拉升评分调整工作台 API
export const intradayScoreWorkbenchApi = {
  getSummary: (params) => api.get('/api/intraday-score-workbench/summary', { params }),
  getAutoTune: (params) => api.get('/api/intraday-score-workbench/auto-tune', { params }),
  getVersions: (params) => api.get('/api/intraday-score-workbench/versions', { params }),
  recomputeVersion: (data) => api.post('/api/intraday-score-workbench/versions/recompute', data),
  compareVersions: (params) => api.get('/api/intraday-score-workbench/compare', { params }),
  // 中文注释：预览（不落库）+ 激活到线上 + 查询当前激活版本 + 回填后验。
  previewRecompute: (data) => api.post('/api/intraday-score-workbench/preview', data),
  activateVersion: (versionKey) => api.post(`/api/intraday-score-workbench/versions/${versionKey}/activate`),
  getActiveVersion: (params) => api.get('/api/intraday-score-workbench/active-version', { params }),
  recomputeOutcomes: (data) => api.post('/api/intraday-score-workbench/recompute-outcomes', data),
  // 中文注释：把自动调权结果一键写入线上配置（阈值 + 维度满分），无需先保存版本。
  applyAutoTuneToLive: (data) => api.post('/api/intraday-score-workbench/auto-tune/apply-to-live', data)
}

// 长线股票管理 API
export const longTermStockApi = {
  list: () => api.get('/api/long-term-stocks'),
  add: (data) => api.post('/api/long-term-stocks', data),
  remove: (stockCode) => api.delete(`/api/long-term-stocks/${stockCode}`)
}

export default api

// Named export for backward compatibility
export const apiClient = api

